import React, {
  createContext,
  PropsWithChildren,
  Ref,
  RefCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { MapRef, ViewStateChangeEvent } from "react-map-gl/mapbox-legacy";
import { useComposedRefs } from "../../../hooks/useComposedRefs";
import { useIsScreenSize } from "../../../hooks/useWindowSize";
import { AuthContext } from "../../Authorization/AuthContext";
import {
  Bounds,
  calculateViewport,
  DEFAULT_VIEWPORT,
  resolveViewportWithPadding,
  useViewport,
  Viewport,
  ViewportOrBounds,
} from "./viewportHook";

export type MapViewportContextType = {
  mapRef: RefCallback<MapRef>;
  initialViewport: Viewport;
  syncedViewport: Viewport;
  onMove: (mapEvent: ViewStateChangeEvent) => void;
  zoomTo: (viewport: ViewportOrBounds) => void;
  setInfoPanelOpen: (infoPanelOpen: boolean) => void;
};

export const MapViewportContext = createContext<MapViewportContextType>({
  mapRef: () => {},
  initialViewport: DEFAULT_VIEWPORT,
  syncedViewport: DEFAULT_VIEWPORT,
  onMove: () => {},
  zoomTo: () => {},
  setInfoPanelOpen: () => {},
});

type MapViewportContextProviderProps = PropsWithChildren<{
  initialViewport: ViewportOrBounds;
  initialInfoPanelOpen?: boolean;
  refsToCompose?: Ref<MapRef>[];
}>;

export const MapViewportContextProvider = ({
  initialViewport,
  initialInfoPanelOpen = false,
  refsToCompose = [],
  children,
}: MapViewportContextProviderProps) => {
  const { account } = useContext(AuthContext);
  const accountBounds = account ? (account.bounds as Bounds) : undefined;
  const mapRef = useRef<MapRef | null>(null);
  const composedRef = useComposedRefs(mapRef, ...refsToCompose);
  const isDesktop = useIsScreenSize("desktop");

  const [infoPanelOpen, setInfoPanelOpen] = useState(initialInfoPanelOpen);

  const resolvedInitialViewport = useMemo(() => {
    const viewportWithPadding = calculateViewport({
      viewport: initialViewport,
      infoPanelOpen,
      isDesktop,
    });
    return resolveViewportWithPadding(viewportWithPadding).viewport;
  }, [initialViewport, infoPanelOpen, isDesktop]);

  const { onMove, zoomTo, syncedViewport } = useViewport({
    mapRef,
    accountBounds,
    initialViewport: resolvedInitialViewport,
    infoPanelOpen,
    isDesktop,
  });

  return (
    <MapViewportContext.Provider
      value={{
        mapRef: composedRef,
        initialViewport: resolvedInitialViewport,
        syncedViewport,
        onMove,
        zoomTo,
        setInfoPanelOpen,
      }}
    >
      {children}
    </MapViewportContext.Provider>
  );
};
